"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { useAuth } from "../context/AuthContext";
import { useQueryUserProfile } from "../hooks/query/queries";
import { usePortfolioEstimate } from "../context/PortfolioEstimateContext";
import { QuickActions } from "./quick-actions";
import { useWalletAssets } from "../hooks/query/queries";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import CurrencyFormatter from "../services/currencyFormatterService";
import { useCurrencySelection } from "../context/CurrencySelectionContext";

export function PortfolioSummary() {
  const { portfolioData: walletData, isLoading: isLoadingBalance } =
    usePortfolioEstimate();

  const { data: walletAssetsData, isLoading: isLoadingAssets } =
    useWalletAssets();
  const assetsForUse = useMemo(
    () => walletAssetsData?.assets || [],
    [walletAssetsData?.assets]
  );
  const { selectedAsset, setSelectedAsset } = useCurrencySelection();
  const [firstTimeUser, setFirstTimeUser] = useState(true);
  const { userProfile, settings } = useAuth();

  const selectedAssetNames = ["USDT", "South African Rand"];

  useEffect(() => {
    if (assetsForUse.length > 0 && !selectedAsset) {
      const zarAsset = assetsForUse.find(
        (asset) =>
          asset.assetName === "South African Rand" || asset.asset === "ZAR"
      );
      if (zarAsset) {
        setSelectedAsset(zarAsset);
      } else if (assetsForUse.length > 0) {
        setSelectedAsset(assetsForUse[0]);
      }
    }
  }, [assetsForUse, selectedAsset, setSelectedAsset]);

  const handleSelect = (asset) => {
    setSelectedAsset(asset);
  };

  const formattedPortfolioValue = useMemo(() => {
    if (!walletAssetsData || !selectedAsset) {
      return "R 0.00";
    }

    if (settings?.digitalWallet?.showBalances === false) {
      return "****";
    }

    const formatter = new CurrencyFormatter({
      locale: "en-US",
      decimalPlaces: 2,
      cryptocurrencyDecimalPlaces: 2,
      stablecoins: ["USDT", "USDC", "DAI"],
    });

    const selectedAssetCode = selectedAsset.asset?.toUpperCase();
    let estimate = 0;

    if (selectedAssetCode === "ZAR") {
      estimate = walletAssetsData.zarEstimate || 0;
      return formatter.format(estimate, "fiat", "ZAR");
    } else if (selectedAssetCode === "USDT") {
      estimate = walletAssetsData.usdtEstimate || 0;
      return formatter.format(estimate, "crypto", "USDT", { showSymbol: true });
    }

    estimate = walletAssetsData.zarEstimate || 0;
    return formatter.format(estimate, "fiat", "ZAR");
  }, [walletAssetsData, selectedAsset, settings]);

  useQueryUserProfile();

  useEffect(() => {
    if (walletData?.portfolioEstimateRaw > 0) {
      setFirstTimeUser(false);
    }
  }, [walletData]);

  return (
    <>
      {firstTimeUser && walletData && (
        <div className="mb-5 text-[#33125F] text-[40px] font-semibold hidden md:block">
          Welcome {userProfile?.personal_info?.name}
        </div>
      )}
      <div
        className={`rounded-2xl border border-[#EBE9EF] mb-8 p-6 md:p-8 md:h-[325px] min-h-[372px] flex flex-col justify-between ${!firstTimeUser && walletData ? "old-user-bg-dashboard-sm" : "bg-dashboard-sm"}`}
      >
        <div
          className={`text-[24px] font-semibold ${!firstTimeUser && walletData ? "text-white" : "text-[#33125F]"} mb-4 md:mb-0`}
        >
          Portfolio value
        </div>
        <div>
          <div className="text-[#EBE9EF]">
            <div className="mb-4 md:mb-6">
              <div className="flex items-center space-x-2 md:space-x-8 justify-between md:justify-start">
                {isLoadingBalance || isLoadingAssets ? (
                  <Skeleton className="w-48 md:w-64 h-10 md:h-12 rounded-full bg-accent/30" />
                ) : (
                  <div className="text-[40px] md:text-[72px] font-bold">
                    {formattedPortfolioValue}
                  </div>
                )}

                {!firstTimeUser &&
                  (isLoadingAssets ? (
                    <Skeleton className="w-16 md:w-20 h-8 md:h-10 rounded-full bg-accent/30" />
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-white bg-transparent border-secondary/50 rounded-full hover:bg-background/30 hover:text-white text-xs md:text-sm h-[44px] md:h-[48px] pl-5 pr-3 md:pl-5 md:pr-3"
                        >
                          {selectedAsset?.asset?.toUpperCase() || "ZAR"}{" "}
                          <ChevronDown className="ml-1 h-3 w-3 md:h-4 md:w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        sideOffset={8}
                        className="rounded-lg max-h-80 overflow-y-auto"
                      >
                        {assetsForUse
                          ?.filter((asset) =>
                            selectedAssetNames.includes(asset.assetName)
                          )
                          .map((asset) => (
                            <DropdownMenuItem asChild key={asset.id}>
                              <button
                                onClick={() => handleSelect(asset)}
                                className="w-full text-left flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent hover:border-rose-200"
                              >
                                <img
                                  src={asset.image}
                                  alt={asset.assetName}
                                  className="w-6 h-6"
                                />
                                <div className="flex flex-col text-left">
                                  <span>{asset.assetName}</span>
                                  <span className="text-xs text-gray-500">
                                    {asset.asset?.toUpperCase()}
                                  </span>
                                </div>
                              </button>
                            </DropdownMenuItem>
                          ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ))}
              </div>
            </div>
          </div>
          {isLoadingBalance ? (
            <div className="grid grid-cols-4 md:grid-cols-4 gap-3 md:gap-4">
              <Skeleton className="w-full h-20 md:h-16 rounded-full md:rounded-lg bg-accent/30" />
              <Skeleton className="w-full h-20 md:h-16 rounded-full md:rounded-lg bg-accent/30" />
              <Skeleton className="w-full h-20 md:h-16 rounded-full md:rounded-lg bg-accent/30" />
              <Skeleton className="w-full h-20 md:h-16 rounded-full md:rounded-lg bg-accent/30" />
            </div>
          ) : (
            <QuickActions />
          )}
        </div>
      </div>
    </>
  );
}
